import type { ReactNode } from 'react'
import { useCallback } from 'react'
import { Text, View } from 'react-native'
import Constants from 'expo-constants'
import { getVoiceAssessmentSessionDefinitionId } from '@/src/config/voice-assessment-session'
import {
  fetchDashboardWeeks,
  fetchMorningRoutineSubject,
} from '@/src/dal/contentful-dashboard-dal'
import { useStartVoiceAssessmentSession } from '@/src/features/voice-assessment/hooks/useStartVoiceAssessmentSession'
import { useContentfulLocale } from '@/src/hooks/useContentfulLocale'
import { useDeveloperMode } from '@/src/hooks/useDeveloperMode'
import { useRequiredAppSessionUser } from '@/src/hooks/useRequiredAppSessionUser'
import {
  buildDailyWarmupExercise,
  buildProgressCountMap,
  resolveContentWeekFromActiveWeek,
  resolveDashboardWeeks,
  resolveProgramWeekCount,
} from '@/src/queries/dashboard-queries'
import { progressSnapshotQueryOptions } from '@/src/queries/progress-queries'
import { queryKeys } from '@/src/queries/query-keys'
import { sessionDefinitionQueryOptions } from '@/src/queries/session-queries'
import { appQueryOptions } from '@/src/queries/view-queries'
import { warmupWeekdayCompletionQueryOptions } from '@/src/queries/warmup-queries'
import { captureException } from '@/src/utils/sentry'
import { queryOptions, useSuspenseQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { ExerciseCard } from '../../../components/ExerciseCard'
import { Icon, symbolName } from '../../../components/ui/Icon'
import { QuerySuspenseBoundary } from '../../../components/ui/QuerySuspenseBoundary'
import { SectionErrorState } from '../../../components/ui/SectionErrorState'
import { colors } from '../../../theme'
import { body1 } from '../../../theme/typography'
import { DashboardExerciseCard } from '../../../types/dashboard'
import {
  getCompletedWeekdaysFromApiCompletion,
  getWeekdayFromDate,
} from '../../../utils/daily-warmup-completion'
import { getCurrentYearWeek } from '../../../utils/year-week'
import { useSessionReminderBanner } from '../hooks/useSessionReminderBanner'
import { DailyWarmupCard } from './DailyWarmupCard'
import { MeasurementReminderBanner } from './MeasurementReminderBanner'

type WeeklyExerciseWithDisplay = DashboardExerciseCard & {
  icon: ReactNode
  subtitle: string
}

type DashboardExerciseItemProps = Readonly<{
  exercise: DashboardExerciseCard
  getExerciseIcon?: (exercise: DashboardExerciseCard) => ReactNode
  getExerciseSubtitle?: (exercise: DashboardExerciseCard) => string
  handleExercisePress: (exercise: DashboardExerciseCard) => void
  hasPurchased: boolean
  icon?: ReactNode
  subtitle?: string
}>

type LockedExerciseIconProps = Readonly<{
  hasPurchased: boolean
}>

type DailyWarmupCardSectionProps = Readonly<{
  handleExercisePress: (exercise: DashboardExerciseCard) => void
}>

type WeeklyExercisesSectionProps = Readonly<{
  children?: ReactNode
  handleExercisePress: (exercise: DashboardExerciseCard) => void
  hasPurchased: boolean
  weeklyExercises: WeeklyExerciseWithDisplay[]
  weekPickerSlot?: ReactNode
}>

type WeeklyExercisesSectionSkeletonProps = Readonly<{
  skeletonCount?: number
  weekPickerSlot?: ReactNode
}>

function LockedExerciseIcon({ hasPurchased }: LockedExerciseIconProps) {
  if (hasPurchased) {
    return <View />
  }

  return <Icon name={symbolName('lock', 'lock')} size={20} color="muted" />
}

function DashboardExerciseItem({
  exercise,
  getExerciseIcon,
  getExerciseSubtitle,
  handleExercisePress,
  hasPurchased,
  icon,
  subtitle,
}: DashboardExerciseItemProps) {
  const resolvedSubtitle = subtitle ?? getExerciseSubtitle?.(exercise) ?? ''
  const resolvedIcon = icon ?? getExerciseIcon?.(exercise)
  return (
    <ExerciseCard
      title={exercise.title}
      subtitle={resolvedSubtitle}
      icon={resolvedIcon}
      onPress={() => handleExercisePress(exercise)}
      isCompleted={exercise.isCompleted}
      disabled={!hasPurchased}
      rightIcon={<LockedExerciseIcon hasPurchased={hasPurchased} />}
    />
  )
}

function MeasurementReminderSectionSkeleton() {
  return (
    <View
      testID="measurement-reminder-skeleton"
      className="mb-2 min-h-20 rounded-lg border px-4 py-5"
      style={{
        borderColor: colors.coreSecondary,
        backgroundColor: colors.secondary700,
        boxShadow: '0px 2px 5px 0px #0000000D',
      }}
    >
      <View className="flex-row items-center justify-between">
        <View className="min-w-0 flex-1 pr-4">
          <View
            className="rounded"
            style={{
              width: '46%',
              height: 16,
              backgroundColor: colors.buttonSecondary,
            }}
          />
          <View
            className="mt-2 rounded"
            style={{
              width: '64%',
              height: 14,
              backgroundColor: colors.buttonSecondary,
            }}
          />
        </View>
        <View
          className="rounded-full"
          style={{
            width: 24,
            height: 24,
            backgroundColor: colors.buttonSecondary,
          }}
        />
      </View>
    </View>
  )
}

function isVoiceAssessmentBannerForcedForBuild(): boolean {
  const buildExtra = Constants.expoConfig?.extra?.build as
    | {
        flavour?: string
      }
    | undefined
  const flavour = buildExtra?.flavour?.trim()

  return flavour != null && flavour !== 'prod'
}

function MeasurementReminderDurationBoundary({
  onPress,
  sessionDefinitionId,
}: Readonly<{
  onPress: () => void
  sessionDefinitionId: number
}>) {
  const { t } = useTranslation(['dashboard', 'common'])

  return (
    <QuerySuspenseBoundary
      loadingFallback={<MeasurementReminderSectionSkeleton />}
      errorFallback={({ reset }) => (
        <SectionErrorState
          testID="measurement-reminder-error"
          title={t('dashboard:measurement_reminder_error_title')}
          message={t('dashboard:measurement_reminder_error_message')}
          onRetry={reset}
        />
      )}
      onError={(error) => {
        captureException(error, {
          feature: 'dashboard',
          tags: { component: 'MeasurementReminderSection' },
        })
      }}
    >
      <MeasurementReminderDurationLoader
        onPress={onPress}
        sessionDefinitionId={sessionDefinitionId}
      />
    </QuerySuspenseBoundary>
  )
}

function MeasurementReminderDurationLoader({
  onPress,
  sessionDefinitionId,
}: Readonly<{
  onPress: () => void
  sessionDefinitionId: number
}>) {
  const language = useContentfulLocale()
  const { data: voiceAssessmentSessionDefinition } = useSuspenseQuery(
    sessionDefinitionQueryOptions(sessionDefinitionId, language),
  )

  return (
    <MeasurementReminderBanner
      estimatedDurationMinutes={voiceAssessmentSessionDefinition.timeEstimate}
      onPress={onPress}
    />
  )
}

export function MeasurementReminderSection() {
  const { t } = useTranslation(['dashboard', 'common'])

  return (
    <QuerySuspenseBoundary
      loadingFallback={<MeasurementReminderSectionSkeleton />}
      errorFallback={({ reset }) => (
        <SectionErrorState
          testID="measurement-reminder-error"
          title={t('dashboard:measurement_reminder_error_title')}
          message={t('dashboard:measurement_reminder_error_message')}
          onRetry={reset}
        />
      )}
      onError={(error) => {
        captureException(error, {
          feature: 'dashboard',
          tags: { component: 'MeasurementReminderSection' },
        })
      }}
    >
      <ReadyMeasurementReminderSection />
    </QuerySuspenseBoundary>
  )
}

function ReadyMeasurementReminderSection() {
  const language = useContentfulLocale()
  const isDeveloperModeEnabled = useDeveloperMode()
  const { backendUser } = useRequiredAppSessionUser()
  const { data: app } = useSuspenseQuery(appQueryOptions(language))
  const sessionDefinitionId = getVoiceAssessmentSessionDefinitionId({
    app,
    user: backendUser,
  })
  const { startVoiceAssessmentSession } = useStartVoiceAssessmentSession()
  const { shouldShow } = useSessionReminderBanner({
    sessionDefinitionId,
  })
  const handlePress = useCallback(() => {
    startVoiceAssessmentSession()
  }, [startVoiceAssessmentSession])

  if (
    !shouldShow &&
    !isVoiceAssessmentBannerForcedForBuild() &&
    !isDeveloperModeEnabled
  ) {
    return null
  }

  return (
    <MeasurementReminderDurationBoundary
      onPress={handlePress}
      sessionDefinitionId={sessionDefinitionId}
    />
  )
}

function getWarmupPressHandler(
  warmupExercise: DashboardExerciseCard | null,
  handleExercisePress: (exercise: DashboardExerciseCard) => void,
) {
  if (!warmupExercise) {
    return undefined
  }

  return () => handleExercisePress(warmupExercise)
}

function DailyWarmupCardSectionSkeleton() {
  return (
    <View
      testID="daily-warmup-skeleton"
      className="mb-2 rounded-lg px-4 py-4 bg-background-secondary"
      style={{
        minHeight: 112,
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
      }}
    >
      <View className="flex-row items-center justify-between">
        <View className="min-w-0 flex-1 pr-4">
          <View
            className="rounded"
            style={{
              width: '52%',
              height: 16,
              backgroundColor: colors.buttonSecondary,
            }}
          />
          <View
            className="mt-2 rounded"
            style={{
              width: '36%',
              height: 12,
              backgroundColor: colors.buttonSecondary,
            }}
          />
        </View>
        <View
          className="rounded-full"
          style={{
            width: 24,
            height: 24,
            backgroundColor: colors.buttonSecondary,
          }}
        />
      </View>
      <View className="mt-4 flex-row justify-between">
        {Array.from({ length: 7 }, (_, index) => (
          <View
            key={`daily-warmup-day-skeleton-${index + 1}`}
            className="rounded-full"
            style={{
              width: 24,
              height: 24,
              backgroundColor: colors.buttonSecondary,
            }}
          />
        ))}
      </View>
    </View>
  )
}

export function DailyWarmupCardSection(props: DailyWarmupCardSectionProps) {
  const { t } = useTranslation(['dashboard', 'common'])

  return (
    <QuerySuspenseBoundary
      loadingFallback={<DailyWarmupCardSectionSkeleton />}
      errorFallback={({ reset }) => (
        <SectionErrorState
          testID="daily-warmup-error"
          title={t('dashboard:daily_warmup_error_title')}
          message={t('dashboard:daily_warmup_error_message')}
          onRetry={reset}
        />
      )}
      onError={(error) => {
        captureException(error, {
          feature: 'dashboard',
          tags: { component: 'DailyWarmupCardSection' },
        })
      }}
    >
      <ReadyDailyWarmupCardSection {...props} />
    </QuerySuspenseBoundary>
  )
}

function ReadyDailyWarmupCardSection({
  handleExercisePress,
}: DailyWarmupCardSectionProps) {
  const { backendUser } = useRequiredAppSessionUser()
  const language = useContentfulLocale()
  const { data: weeks } = useSuspenseQuery(
    queryOptions({
      queryKey: queryKeys.dashboard.weeks.byLocale(language),
      queryFn: () => fetchDashboardWeeks(language),
    }),
  )

  const programWeekCount = resolveProgramWeekCount(weeks)
  const currentContentWeek = resolveContentWeekFromActiveWeek(
    backendUser.activeWeek,
    programWeekCount,
  )
  const resolvedWeek = resolveDashboardWeeks({
    currentProgramWeek: currentContentWeek,
    selectedWeek: currentContentWeek,
    weeks,
  })

  const { data: progressSnapshot } = useSuspenseQuery(
    progressSnapshotQueryOptions(resolvedWeek.yearWeek),
  )
  const date = new Date()
  const progressCountMap = buildProgressCountMap(progressSnapshot.progressItems)

  const { data: morningSubject } = useSuspenseQuery(
    queryOptions({
      queryKey: queryKeys.dashboard.morningRoutine.byLocale(language),
      queryFn: () => fetchMorningRoutineSubject(language),
    }),
  )

  const warmupExercise = buildDailyWarmupExercise({
    morningSubject,
    activeWeek: currentContentWeek,
    progressCountMap,
    date,
  })

  const yearWeek = getCurrentYearWeek()
  const { data: warmupWeekdayCompletion } = useSuspenseQuery(
    warmupWeekdayCompletionQueryOptions(yearWeek),
  )
  const todayWarmupWeekday = getWeekdayFromDate(new Date())
  const isTodayDailyWarmupCompleted =
    warmupWeekdayCompletion?.[todayWarmupWeekday] ?? false
  const dailyWarmupCompletedWeekdays = getCompletedWeekdaysFromApiCompletion(
    warmupWeekdayCompletion,
  )

  return (
    <DailyWarmupCard
      completedWeekdays={dailyWarmupCompletedWeekdays}
      isTodayCompleted={isTodayDailyWarmupCompleted}
      warmupDurationMinutes={warmupExercise?.lengthMinutes ?? null}
      onPress={getWarmupPressHandler(warmupExercise, handleExercisePress)}
      yearWeek={yearWeek}
    />
  )
}

export function WeeklyExercisesSection({
  children,
  handleExercisePress,
  hasPurchased,
  weeklyExercises,
  weekPickerSlot,
}: WeeklyExercisesSectionProps) {
  const { t } = useTranslation('dashboard')
  if (weeklyExercises.length === 0) {
    return null
  }

  return (
    <View className="mb-8">
      <View className="mb-3.5 flex-row items-center justify-between">
        <Text className="text-ink" style={body1}>
          {t('weekly_exercise_section')}
        </Text>
        {weekPickerSlot}
      </View>
      {children}
      {weeklyExercises.map((exercise) => (
        <DashboardExerciseItem
          key={exercise.id}
          exercise={exercise}
          handleExercisePress={handleExercisePress}
          hasPurchased={hasPurchased}
          icon={exercise.icon}
          subtitle={exercise.subtitle}
        />
      ))}
    </View>
  )
}

function WeeklyExerciseSkeletonCard({ testID }: { testID: string }) {
  return (
    <View
      testID={testID}
      className="bg-background-secondary py-4.5 px-4 rounded-lg flex-row items-center border border-gray-50 min-h-20"
      style={{
        marginBottom: 8,
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
      }}
    >
      <View
        className="mr-3 rounded-full"
        style={{
          width: 32,
          height: 32,
          backgroundColor: colors.buttonSecondary,
        }}
      />
      <View className="flex-1">
        <View
          className="rounded"
          style={{
            width: '58%',
            height: 16,
            marginBottom: 8,
            backgroundColor: colors.buttonSecondary,
          }}
        />
        <View
          className="rounded"
          style={{
            width: '44%',
            height: 12,
            backgroundColor: colors.buttonSecondary,
          }}
        />
      </View>
    </View>
  )
}

export function WeeklyExercisesSectionSkeleton({
  skeletonCount = 3,
  weekPickerSlot,
}: WeeklyExercisesSectionSkeletonProps) {
  const { t } = useTranslation('dashboard')

  return (
    <View className="mb-8">
      <View className="mb-3.5 flex-row items-center justify-between">
        <Text className="text-ink" style={body1}>
          {t('weekly_exercise_section')}
        </Text>
        {weekPickerSlot}
      </View>
      {Array.from({ length: skeletonCount }, (_, index) => index + 1).map(
        (skeletonNumber) => (
          <WeeklyExerciseSkeletonCard
            key={`weekly-exercise-skeleton-${skeletonNumber}`}
            testID={`weekly-exercise-skeleton-${skeletonNumber - 1}`}
          />
        ),
      )}
    </View>
  )
}
